import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import BackToTop from '@/components/feature/BackToTop';

const SITE_URL = import.meta.env.VITE_SITE_URL ?? 'https://greenlyelectronicsrecycling.com';

const acceptedItems = [
  'Computer Recycling',
  'Television',
  'UPS Batteries',
  'Cell Phones',
  'Cables / Cords',
  'Servers',
  'Network Switches',
  'Printers / Copiers (Fee May Apply)',
  'Laptops',
  'Monitors',
  'Tablets',
  'Hard Drives',
  'IT Hardware',
  'Keyboards & Mice',
];

const notAccepted = [
  'Motor Oil',
  'Microwave',
  'Wooden Speakers',
  'Printer Ink & Toner',
  'Light Bulbs',
  'Single-use Alkaline Batteries',
  'Items Containing Liquid Mercury',
  'Hazardous Waste: Paint',
];

interface FaqItem {
  q: string;
  a: string;
}

const faqCategories: { category: string; icon: string; items: FaqItem[] }[] = [
  {
    category: 'Drop-Off & Pickup',
    icon: 'ri-truck-line',
    items: [
      {
        q: 'Where can I drop off electronics for recycling in Los Angeles?',
        a: 'Greenly has two convenient drop-off locations in Los Angeles. Our Central LA location is at 2542 W. 3rd Street, Los Angeles, CA 90057 (near Koreatown and DTLA). Our South LA location is at 4902 W. Slauson Ave, Los Angeles, CA 90056 (near Inglewood and Culver City). Both are open Mon–Fri 8am–5pm and Sat 9am–2pm. No appointment needed. See the E-Waste Guide for more details.',
      },
      {
        q: 'Do you offer free e-waste pickup for businesses?',
        a: 'Yes. Greenly offers free e-waste pickup for qualifying businesses throughout Los Angeles County. Typically, free pickup is available for loads of 10 or more devices, or a significant volume of e-waste. Call (213) 600-1000 to confirm eligibility and schedule your pickup.',
      },
      {
        q: 'How quickly can you schedule a pickup?',
        a: 'We typically schedule pickups within 1–3 business days. For urgent needs or large-scale decommissions, call us directly at (213) 600-1000 and we\'ll do our best to accommodate same-day or next-day service.',
      },
      {
        q: 'Do you pick up from residential addresses?',
        a: 'Our free pickup service is primarily for businesses, schools, hospitals, and organizations. Residential customers are welcome to drop off electronics at either of our two LA locations at no charge. For large residential cleanouts, call us to discuss options.',
      },
      {
        q: 'What cities do you serve for e-waste pickup?',
        a: 'We serve all of Los Angeles County, including Burbank, Glendale, Pasadena, Long Beach, Santa Monica, Culver City, Inglewood, Torrance, Downtown LA, the San Fernando Valley, Silver Lake, and all surrounding cities. Call (213) 600-1000 to confirm service in your area.',
      },
    ],
  },
  {
    category: 'Accepted Items',
    icon: 'ri-recycle-line',
    items: [
      {
        q: 'What electronics can I recycle at Greenly?',
        a: 'We accept computers (desktops, laptops, Macs), televisions (all types including CRT), UPS batteries, cell phones, cables and cords, servers, network switches, printers and copiers (fee may apply), monitors, tablets, hard drives, IT hardware, and keyboards & mice. If you\'re unsure about a specific item, call us at (213) 600-1000.',
      },
      {
        q: 'Do you accept CRT (tube) televisions?',
        a: 'Yes. We accept all CRT televisions and monitors. Note that CalRecycle requires us to collect basic personal information (name, address, phone number as they appear on your driver\'s license) for all TV and monitor screen drop-offs that were used in California.',
      },
      {
        q: 'Do you accept printers and copiers?',
        a: 'Yes, we accept printers and copiers, though a fee may apply depending on the type and size. Call (213) 600-1000 before bringing in large copiers or multifunction printers to confirm pricing.',
      },
      {
        q: 'What items do you NOT accept?',
        a: 'We cannot accept motor oil, microwaves, wooden speakers, printer ink & toner, light bulbs, single-use alkaline batteries, items containing liquid mercury, or hazardous waste such as paint. For hazardous waste disposal, contact LA County\'s SAFE Centers.',
      },
      {
        q: 'Do you accept batteries?',
        a: 'We accept rechargeable batteries including laptop batteries, phone batteries, lithium-ion batteries, and UPS batteries. We do NOT accept single-use alkaline batteries (AA, AAA, C, D, 9V). For alkaline battery recycling, check your local hardware store or Home Depot.',
      },
    ],
  },
  {
    category: 'Data Security',
    icon: 'ri-shield-check-line',
    items: [
      {
        q: 'What happens to the data on my hard drives?',
        a: 'All hard drives and storage devices we receive are wiped to DoD 5220.22-M standards as part of our standard process. For businesses requiring certified data destruction, we offer physical hard drive shredding with a Certificate of Destruction documenting each device by serial number.',
      },
      {
        q: 'Do you provide a Certificate of Data Destruction?',
        a: 'Yes. We provide a Certificate of Data Destruction for all hard drives that undergo physical shredding. The certificate includes device serial numbers, destruction method, date, and our CalRecycle certification number. This is available for both pickup and drop-off customers.',
      },
      {
        q: 'Is your data destruction HIPAA compliant?',
        a: 'Yes. Our data destruction process meets HIPAA requirements for the disposal of electronic protected health information (ePHI). We provide the documentation required for HIPAA compliance audits, including chain-of-custody records and Certificates of Destruction.',
      },
      {
        q: 'Should I wipe my hard drive before dropping it off?',
        a: 'You don\'t need to — we handle data destruction as part of our process. However, if you want extra peace of mind, you\'re welcome to wipe your drive before dropping it off. For certified destruction with documentation, our physical shredding service is the most secure option.',
      },
    ],
  },
  {
    category: 'Pricing & Certification',
    icon: 'ri-price-tag-3-line',
    items: [
      {
        q: 'Is electronics recycling free at Greenly?',
        a: 'Drop-off recycling is free for most items. Business pickup is free for qualifying loads (typically 10+ devices). Some items like large printers, copiers, and certain specialty equipment may have a fee. Call (213) 600-1000 to confirm pricing for your specific items.',
      },
      {
        q: 'Do you provide a Certificate of Recycling?',
        a: 'Yes. We provide a Certificate of Recycling for all business pickups and upon request for drop-off customers. The certificate documents the items recycled, date, and our CalRecycle certification number (CEWD-127531). This is useful for corporate sustainability reporting and compliance.',
      },
      {
        q: 'Is Greenly CalRecycle certified?',
        a: 'Yes. Greenly Electronics Recycling is certified by CalRecycle under certification number CEWD-127531. This means we meet California\'s strict standards for e-waste recycling, including proper handling of hazardous materials and zero-landfill practices.',
      },
      {
        q: 'Do you offer on-site data destruction?',
        a: 'Yes. For businesses with strict security requirements, we offer on-site hard drive shredding where our certified technicians destroy drives at your location. You can witness the destruction and receive a Certificate of Destruction on the spot. Call (213) 600-1000 to arrange.',
      },
    ],
  },
  {
    category: 'Business & Organizations',
    icon: 'ri-building-2-line',
    items: [
      {
        q: 'Do you serve schools and universities?',
        a: 'Yes. We regularly serve K-12 schools, community colleges, and universities throughout Los Angeles County with free e-waste pickup. We provide recycling certificates for district and institutional records, and can coordinate with facilities management for large-scale cleanouts.',
      },
      {
        q: 'Do you serve hospitals and medical facilities?',
        a: 'Yes. We provide HIPAA-compliant data destruction and e-waste recycling for hospitals, clinics, and medical offices throughout LA County. We understand the strict data security requirements of healthcare organizations and provide the documentation needed for compliance.',
      },
      {
        q: 'Can you handle a full office or data center decommission?',
        a: 'Absolutely. We handle everything from small office cleanouts to full data center decommissions. We provide a project coordinator, flexible scheduling, full chain-of-custody documentation, and Certificates of Recycling and Destruction for every device. Call (213) 600-1000 to discuss your project.',
      },
      {
        q: 'Do you work with property managers and real estate companies?',
        a: 'Yes. We regularly work with property managers, real estate companies, and building owners to handle tenant cleanouts and building-wide e-waste collection. We can coordinate access with building management and provide documentation for all items removed.',
      },
    ],
  },
];

const allFaqItems: FaqItem[] = faqCategories.flatMap((cat) => cat.items);

const FAQJsonLd = () => (
  <script type="application/ld+json">{JSON.stringify({
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": `${SITE_URL}/faq#webpage`,
        "url": `${SITE_URL}/faq`,
        "name": "Items We Recycle & E-Waste FAQ | Los Angeles Electronics Recycling | Greenly",
        "description": "See what electronics Greenly accepts for recycling in Los Angeles. Computers, TVs, batteries, phones, servers, hard drives & more. Plus answers to common e-waste questions. Call (213) 600-1000.",
        "isPartOf": { "@id": `${SITE_URL}/#website` },
        "breadcrumb": {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
            { "@type": "ListItem", "position": 2, "name": "Items We Recycle & FAQ", "item": `${SITE_URL}/faq` }
          ]
        }
      },
      {
        "@type": "FAQPage",
        "@id": `${SITE_URL}/faq#faqpage`,
        "mainEntity": allFaqItems.map((faq) => ({
          "@type": "Question",
          "name": faq.q,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.a,
          },
        })),
      },
    ],
  })}</script>
);

const FAQPage = () => {
  const [openItem, setOpenItem] = useState<string | null>(null);

  const toggle = (key: string) => setOpenItem(openItem === key ? null : key);

  return (
    <>
      <title>Items We Recycle &amp; E-Waste FAQ | Los Angeles Electronics Recycling | Greenly</title>
      <meta name="description" content="What electronics does Greenly recycle in Los Angeles? Computers, TVs, batteries, phones, servers & more. Plus answers to 20+ common e-waste questions. Call (213) 600-1000." />
      <meta name="keywords" content="items we recycle Los Angeles, e-waste FAQ Los Angeles, electronics recycling accepted items, what can I recycle Los Angeles, e-waste drop off Los Angeles" />
      <meta name="robots" content="index, follow" />
      <meta name="last-modified" content="2026-05-11" />
      <link rel="canonical" href={`${SITE_URL}/faq`} />
      <FAQJsonLd />
      <Navbar />
      <main>

        {/* Hero */}
        <section className="bg-[#0F3D22] pt-28 pb-14 px-4 md:px-6 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <img
              src="https://readdy.ai/api/search-image?query=Electronics%20recycling%20center%20with%20organized%20bins%20of%20computers%20TVs%20and%20devices%2C%20clean%20modern%20facility%2C%20green%20eco-friendly%20environment%2C%20professional%20recycling%20operation%2C%20wide%20angle%20view&width=1400&height=400&seq=faq-hero-bg&orientation=landscape"
              alt="Electronics recycling Los Angeles"
              className="w-full h-full object-cover object-center"
              loading="eager"
            />
          </div>
          <div className="relative z-10 max-w-7xl mx-auto">
            <Breadcrumb items={[{ label: 'Items We Recycle & FAQ' }]} />
            <div className="max-w-3xl mt-4">
              <div className="inline-flex items-center gap-2 bg-white/20 text-white text-xs font-bold px-3 py-1.5 rounded-full mb-4">
                <i className="ri-question-answer-line"></i>
                E-Waste Recycling Guide
              </div>
              <h1 className="text-3xl md:text-4xl font-extrabold text-white leading-tight mb-4">
                Items We Recycle &amp; Common Questions
              </h1>
              <p className="text-green-200 text-base leading-relaxed max-w-2xl">
                Everything you need to know about recycling electronics in Los Angeles — what we accept, how pickup works, data security, and more.
              </p>
            </div>
          </div>
        </section>

        {/* Drop-off card + accepted items */}
        <section className="py-14 bg-[#F4FAF7]">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

              {/* Accepted */}
              <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
                <div className="bg-[#1B6B3A] px-6 py-4">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-lg">
                      <i className="ri-checkbox-circle-line text-white text-lg"></i>
                    </div>
                    <h2 className="text-white font-extrabold text-base">Accepted Items</h2>
                  </div>
                </div>
                <div className="px-6 py-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2.5 gap-x-4">
                    {acceptedItems.map((item) => (
                      <div key={item} className="flex items-center gap-2.5">
                        <div className="w-5 h-5 flex items-center justify-center bg-[#E8F5EE] rounded-full shrink-0">
                          <i className="ri-check-line text-[#1B6B3A] text-xs"></i>
                        </div>
                        <span className="text-sm text-gray-700">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Not accepted + drop-off info */}
              <div className="flex flex-col gap-5">
                <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
                  <div className="bg-red-500 px-6 py-4">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-lg">
                        <i className="ri-close-circle-line text-white text-lg"></i>
                      </div>
                      <h2 className="text-white font-extrabold text-base">Not Accepted</h2>
                    </div>
                  </div>
                  <div className="px-6 py-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2.5 gap-x-4">
                      {notAccepted.map((item) => (
                        <div key={item} className="flex items-center gap-2.5">
                          <div className="w-5 h-5 flex items-center justify-center bg-red-50 rounded-full shrink-0">
                            <i className="ri-close-line text-red-400 text-xs"></i>
                          </div>
                          <span className="text-sm text-gray-700">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* CalRecycle note */}
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-lg shrink-0">
                      <i className="ri-information-line text-amber-600 text-lg"></i>
                    </div>
                    <div>
                      <div className="font-bold text-amber-800 text-sm mb-1">CalRecycle TV & Monitor Note</div>
                      <p className="text-amber-700 text-xs leading-relaxed">
                        All TVs and monitor screens must have been used in California. CalRecycle requires us to collect your Name, Address, and Phone number as they appear on your driver&apos;s license.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Drop-off locations */}
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
              {[
                {
                  name: 'Central LA — W. 3rd Street',
                  addr: '2542 W. 3rd Street, Los Angeles, CA 90057',
                  hours: 'Mon–Fri 8am–5pm · Sat 9am–2pm',
                  phone: '(213) 600-1000',
                  link: 'https://maps.app.goo.gl/1GFb4ZnoPCREHhzy6',
                },
                {
                  name: 'South LA — W. Slauson Ave',
                  addr: '4902 W. Slauson Ave, Los Angeles, CA 90056',
                  hours: 'Mon–Fri 8am–5pm · Sat 9am–2pm',
                  phone: '(213) 600-1000',
                  link: 'https://maps.google.com/?q=4902+W+Slauson+Ave+Los+Angeles+CA+90056',
                },
              ].map((loc) => (
                <div key={loc.name} className="bg-white border border-gray-200 rounded-2xl p-5 flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#E8F5EE] rounded-xl shrink-0">
                    <i className="ri-map-pin-2-fill text-[#1B6B3A] text-lg"></i>
                  </div>
                  <div>
                    <div className="font-extrabold text-gray-900 text-sm">{loc.name}</div>
                    <div className="text-gray-500 text-xs mt-0.5">{loc.addr}</div>
                    <div className="text-gray-400 text-xs mt-0.5">{loc.hours}</div>
                    <a
                      href={loc.link}
                      target="_blank"
                      rel="nofollow noreferrer"
                      className="inline-flex items-center gap-1 text-[#1B6B3A] font-semibold text-xs mt-2 hover:underline cursor-pointer"
                    >
                      <i className="ri-direction-line"></i>Get Directions
                    </a>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA strip */}
            <div className="mt-6 flex flex-col sm:flex-row gap-4">
              <Link
                to="/dropoff-form"
                className="flex-1 flex items-center justify-center gap-2 bg-[#1B6B3A] hover:bg-[#155530] text-white text-sm font-bold py-3.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-map-pin-line"></i>
                Schedule a Drop Off
              </Link>
              <Link
                to="/pickup-request"
                className="flex-1 flex items-center justify-center gap-2 bg-white border border-gray-200 hover:bg-[#F4FAF7] text-[#1B6B3A] text-sm font-bold py-3.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-truck-line"></i>
                Request a Pickup Instead
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ Accordion */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <div className="text-[#1B6B3A] text-xs font-bold uppercase tracking-widest mb-2">Frequently Asked Questions</div>
              <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-3">
                Everything You Need to Know About E-Waste Recycling in LA
              </h2>
              <p className="text-gray-500 text-sm max-w-xl mx-auto">
                Can&apos;t find your answer? Call us at <a href="tel:+12136001000" className="text-[#1B6B3A] font-semibold hover:underline">(213) 600-1000</a> — we&apos;re happy to help.
              </p>
            </div>

            <div className="space-y-10">
              {faqCategories.map((cat) => (
                <div key={cat.category}>
                  {/* Category header */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 flex items-center justify-center bg-[#E8F5EE] rounded-xl shrink-0">
                      <i className={`${cat.icon} text-[#1B6B3A] text-lg`}></i>
                    </div>
                    <h3 className="text-base font-extrabold text-gray-900">{cat.category}</h3>
                    <div className="flex-1 h-px bg-gray-100"></div>
                  </div>

                  {/* Items */}
                  <div className="space-y-2">
                    {cat.items.map((faq, i) => {
                      const key = `${cat.category}-${i}`;
                      const isOpen = openItem === key;
                      return (
                        <div key={key} className="border border-gray-200 rounded-xl overflow-hidden">
                          <button
                            className="w-full flex items-start justify-between gap-4 px-5 py-4 text-left cursor-pointer hover:bg-[#F4FAF7] transition-colors"
                            onClick={() => toggle(key)}
                          >
                            <span className="text-sm font-bold text-gray-900 leading-snug">{faq.q}</span>
                            <div className="w-6 h-6 flex items-center justify-center shrink-0 mt-0.5">
                              <i className={`text-[#1B6B3A] text-base transition-transform duration-200 ${isOpen ? 'ri-subtract-line' : 'ri-add-line'}`}></i>
                            </div>
                          </button>
                          {isOpen && (
                            <div className="px-5 pb-5 pt-1 bg-[#F4FAF7]">
                              <p className="text-gray-600 text-sm leading-relaxed">{faq.a}</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-14 bg-[#1B6B3A]">
          <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-4">
              Ready to Recycle Your Electronics?
            </h2>
            <p className="text-green-100 text-sm md:text-base mb-8 max-w-xl mx-auto">
              Drop off at either of our two LA locations, or request a free business pickup. We serve all of Los Angeles County.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="tel:+12136001000"
                className="inline-flex items-center justify-center gap-2 bg-white text-[#1B6B3A] font-bold px-8 py-3 rounded-md hover:bg-green-50 transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                <i className="ri-phone-fill"></i>Call (213) 600-1000
              </a>
              <Link
                to="/pickup-request"
                className="inline-flex items-center justify-center gap-2 bg-white/20 border border-white/40 text-white font-semibold px-8 py-3 rounded-md hover:bg-white/30 transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                <i className="ri-truck-line"></i>Request Free Pickup
              </Link>
              <Link
                to="/locations"
                className="inline-flex items-center justify-center gap-2 bg-white/20 border border-white/40 text-white font-semibold px-8 py-3 rounded-md hover:bg-white/30 transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                <i className="ri-map-pin-line"></i>Find Drop-Off Location
              </Link>
            </div>
          </div>
        </section>

      </main>
      <Footer />
      <BackToTop />
    </>
  );
};

export default FAQPage;
